// # packages
import * as React from "react";
import { boundMethod } from "autobind-decorator";
import { debounce, get } from "lodash";
import classnames from "classnames";
// # components
import { Spinner } from "@/components/feedback";
import { List, iProps as iListProps } from "@/components/List";
import { ClickEventHandler } from "@/components/ClickEventHandler";

interface iProps<iOption> extends Omit<iListProps<iOption>, "items"> {
  /**
   * Class name applied to the container.
   */
  className?: string;
  /**
   * Puts the component into a disabled state.
   */
  disabled?: boolean;
  /**
   * Whether you can type to filter options.
   *
   * @todo Not yet implemented, currently always true.
   */
  filterable?: true;
  /**
   * Override the function that filters options.
   */
  filter?: (searchQuery: string, item: iOption) => boolean;
  /**
   * Items to display in the dropdown.
   * Can be an array of items, or a method that fetches items asynchronously.
   */
  items: iOption[] | ((searchQuery: string) => Promise<iOption[]>);
  /**
   * Placeholder of the filter input.
   */
  placeholder?: string;
  /**
   * The selected value.
   */
  value?: any;

  onGroupHeaderClick?: (feature: string) => void;
}

interface iState<iOption> {
  flags: {
    hasFocus: boolean;
    isFetchingItems: boolean;
  };
  input: {
    query: string;
  };
  items: iOption[];
}

export class Select<iOption> extends React.Component<
  iProps<iOption>,
  iState<iOption>
> {
  static defaultProps: Partial<iProps<any>> = {
    filter: (searchQuery: string, item: any) => {
      const queryLowercase = searchQuery.toLowerCase();
      const labelLowercase = get(item, "label", "").toLowerCase();
      return labelLowercase.includes(queryLowercase);
    },
    items: [],
    placeholder: "Search...",
    onSelect: () => null,
    renderItem: (item, clickHandler) => {
      const className = classnames("list__item", {
        "is-interactive": !!clickHandler,
      });

      return (
        <div className={className} onClick={clickHandler}>
          {get(item, "label", '[Item has no "label" property]')}
        </div>
      );
    },
    onGroupHeaderClick: (feature: string) => {},
  };

  static displayName = "Select";

  static getDerivedStateFromProps(
    props: iProps<any>,
    state: iState<any>,
  ): iState<any> {
    // Replace items in state from props if they change
    if (typeof props.items === "object") {
      return {
        ...state,
        items: props.items,
      };
    }

    return state;
  }

  public state: iState<iOption> = {
    flags: {
      hasFocus: false,
      isFetchingItems: false,
    },
    input: {
      query: "",
    },
    items: [],
  };

  /**
   * Reference to the container div.
   *
   * @type {React.RefObject<HTMLDivElement>}
   */
  protected $container: React.RefObject<HTMLDivElement> = React.createRef();

  /**
   * Reference to the search input field.
   *
   * @type {React.RefObject<HTMLInputElement>}
   */
  protected $input: React.RefObject<HTMLInputElement> = React.createRef();

  public componentDidMount(): void {
    if (typeof this.props.items === "function") {
      this.fetchItemsDebounced();
    }
  }

  /**
   * Renders the content.
   */
  public render(): React.ReactNode {
    const { className, disabled, value } = this.props;
    const { flags } = this.state;

    const containerClassName = classnames(
      "select",
      "select--options-visible",
      {
        "select--disabled": disabled,
      },
      className,
    );

    return (
      <>
        <div className={containerClassName} ref={this.$container}>
          {this.renderFilterInput()}
          {this.renderOptions()}
        </div>

        {!(!flags.hasFocus && value) ? (
          <ClickEventHandler
            target={this.$container}
            onClickOutside={this.blur}
          />
        ) : null}
      </>
    );
  }

  /**
   * Fetches items if the "items" prop is a function.
   *
   * @returns {Promise<void>}
   */
  protected async fetchItems(search: string = ""): Promise<void> {
    if (typeof this.props.items === "function") {
      try {
        this.setState((state) => {
          state.flags.isFetchingItems = true;
          return state;
        });
        const items = await this.props.items(search);
        this.setState((state) => {
          const stateCopy = { ...state };
          stateCopy.flags.isFetchingItems = false;
          stateCopy.items = items || [];
          return stateCopy;
        });
      } catch (error) {
        this.setState((state) => {
          state.flags.isFetchingItems = false;
          return state;
        });
      }
    }
  }

  /**
   * Wrap this.fetchItems in debounce method so it won't be called
   * an unecessary number of times in quick succession.
   *
   * @var {(search: string) => null | Promise<void>}
   */
  protected fetchItemsDebounced = debounce(this.fetchItems, 300);

  @boundMethod
  protected handleSearchQueryChange(
    evt: React.ChangeEvent<HTMLInputElement>,
  ): void {
    const query = evt.target.value;

    this.setState(
      (state) => ({
        ...state,
        input: { query },
      }),
      () => {
        this.fetchItemsDebounced(query);
      },
    );
  }

  /**
   * Renders the search filter field, or the selected item.
   *
   * @returns {React.ReactElement}
   */
  @boundMethod
  protected renderFilterInput(): React.ReactElement {
    const { renderItem, value } = this.props;
    const { flags } = this.state;

    if (!flags.hasFocus && value) {
      return renderItem(value, () => {
        this.setFocus(true);
      });
    }

    return (
      <input
        className="select__filter-input"
        placeholder={this.props.placeholder}
        onChange={this.handleSearchQueryChange}
        onFocus={this.focus}
        ref={this.$input}
        type="text"
      />
    );
  }

  /**
   * Renders the list of available options.
   *
   * @returns {React.ReactElement}
   */
  @boundMethod
  protected renderOptions(): React.ReactElement {
    const { filter, onSelect, groupBy, renderGroupHeader, renderItem } =
      this.props;
    const { flags, input, items } = this.state;

    const handleSelect = (option: iOption) => {
      onSelect(option);
      this.blur();
    };

    // don't render the options unless this component has focus
    if (!flags.hasFocus) {
      return null;
    }

    // apply search filter to items
    const filteredItems = items.filter(filter.bind(null, input.query));

    if (flags.isFetchingItems) {
      return (
        <div className="select__options">
          <Spinner size="small" />
        </div>
      );
    }

    return (
      <div className="select__options">
        <List
          groupBy={groupBy}
          items={filteredItems}
          onSelect={handleSelect}
          renderGroupHeader={renderGroupHeader}
          onGroupHeaderClick={this.props.onGroupHeaderClick}
          renderItem={renderItem}
        />
      </div>
    );
  }

  /**
   * Puts the component into a focussed or unfocussed state.
   *
   * @param {boolean} hasFocus
   * @returns {void}
   */
  @boundMethod
  protected setFocus(hasFocus: boolean): void {
    this.setState(
      (state) => ({
        ...state,
        flags: {
          ...state.flags,
          hasFocus,
        },
      }),
      () => {
        if (hasFocus && this.$input.current) {
          this.$input.current.focus();
        }
      },
    );
  }

  /**
   * Puts the component in to a focussed state.
   *
   * @returns {void}
   */
  protected focus = this.setFocus.bind(this, true);

  /**
   * Puts the component in to an unfocussed state.
   *
   * @returns {void}
   */
  protected blur = this.setFocus.bind(this, false);
}
